import { useAbility } from '@casl/react';
import React, { ReactNode, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Subject } from 'src/auth/utils/defineAbilityFor';
import { NetworkState } from 'src/constants/network-state';
import { useAppSelector } from 'src/hooks/hooks';
import pages from 'src/router/routes';
import SuspenseLoader from 'switch-fe-shared/components/Suspense/SuspenseLoader';
import { useFeatureFlag } from 'switch-fe-shared/hooks/useFeatureFlag';

import { AbilityContext } from '../context/canContext';

interface RouteGuardProps {
  allowedActions: string;
  children: ReactNode;
  subject: Subject;
}

const RouteGuard: React.FC<RouteGuardProps> = ({
  allowedActions,
  children,
  subject,
}) => {
  const ability = useAbility(AbilityContext);
  const abilityRoleSlice = useAppSelector((state) => state.abilityRole);
  const isUrlNavigationEnabled = useFeatureFlag('ac_url_navigation__release');
  const hasAbilityRoles = abilityRoleSlice.abilityRoles.length > 0;
  const isAbilityLoading =
    abilityRoleSlice.status === NetworkState.NOT_STARTED ||
    abilityRoleSlice.status === NetworkState.LOADING;

  if (
    isUrlNavigationEnabled
      ? isAbilityLoading && !hasAbilityRoles
      : abilityRoleSlice.status !== NetworkState.SUCCESS
  ) {
    return (
      <Suspense fallback={<SuspenseLoader />}>
        <SuspenseLoader />
      </Suspense>
    );
  }

  if (isUrlNavigationEnabled && !hasAbilityRoles) {
    return <Navigate to={`/${pages.status.status404.name}`} />;
  }

  const hasPermission = ability.can(allowedActions, subject);

  if (!hasPermission) {
    toast.error('You do not have permission to view this page');
    return <Navigate to={`/${pages.status.status404.name}`} />;
  }

  return <>{children}</>;
};

export default RouteGuard;
